/**
 * Обзор: четыре числа, откуда выручка и прибыль, что требует внимания.
 *
 * Сигналы вычисляются из ответов отчётов по правилам, которые написаны
 * под каждым сигналом. Нет данных под правило — нет сигнала; выдуманных
 * «обратите внимание» здесь не бывает.
 */

import { Icon } from '../../components/Icon'
import { useState } from 'react'
import { useFinance } from '../context'
import { indexBy, num, rowKey, filterValue, useReport, useSettled, type ReportState } from '../data'
import { usePublishFreshness } from '../freshness'
import { BarRows, ColumnChart, FillRows, SegmentBar } from '../charts'
import { useMediaQuery } from '../motion'
import { Caveat, closedReason, DataNotes, Delta, Kpi, Panel, Segmented, SignalList, SkeletonBars, SkeletonColumns, SkeletonSignals, type Signal } from '../parts'
import { originFrom } from '../Drawer'
import { BUCKETS, OVERDUE_KEYS, useCashByItem, useCashCoverage, useCashTrend, useDebtRelations, useDebtors, useSales, useSalesSource } from '../queries'
import { compareLabel, labelOf, monthLabel, monthsOf, shortMonth } from '../period'
import { missingOf, monthState, valueIn } from '../coverage'
import { noCompareReason, salesEmpty } from '../frame'
import { deltaPercent, deltaPoints, formatDate, formatMoneyShort, formatRub, formatNumber, formatPercent, pluralize, toNumber } from '../../format'

export const SALES_SERIES = [
  { key: 'revenue', label: 'Выручка без НДС', color: 'var(--series-1)' },
  { key: 'gross_profit', label: 'Валовая прибыль', color: 'var(--series-3)' },
]

export const DIMENSION_TITLES: Record<string, string> = {
  direction: 'Направления',
  nomenclature_group: 'Группы',
  branch: 'Филиалы',
}

export const money = (value: number) => formatRub(value)

/** Изменение денежной величины в рублях — для потоков, где знак может смениться. */
export function MoneyDelta({ now, before, label, invert }: { now: unknown; before: unknown; label: string; invert?: boolean }) {
  const a = toNumber(now)
  const b = toNumber(before)
  if (a === null || b === null) return <span className="muted">{label.startsWith('к ') ? 'нет базы для сравнения' : label}</span>
  const diff = a - b
  const good = invert ? diff < 0 : diff > 0
  return (
    <span className="kpi__delta">
      <span className={`delta ${diff === 0 ? 'delta--flat' : good ? 'delta--up' : 'delta--down'}`}>
        <Icon name={diff > 0 ? 'tri-up' : diff < 0 ? 'tri-down' : 'dot'} size={10} />
        {diff > 0 ? '+' : diff < 0 ? '−' : ''}
        {formatMoneyShort(Math.abs(diff))}
      </span>
      <span className="muted">{label}</span>
    </span>
  )
}

export function overdueOf(row: Record<string, unknown> | Record<string, number | string | null>): number {
  return OVERDUE_KEYS.reduce((sum, key) => sum + num(row[key]), 0)
}

export const over60 = (row: Record<string, unknown>) => num(row.overdue_61_90) + num(row.overdue_90_plus)

/** Строки, отсортированные по выручке, и средняя маржа — для полос и сигналов. */
function marginRows(state: ReportState) {
  return [...(state.data?.rows ?? [])]
    .filter((row) => num(row.revenue) > 0)
    .sort((a, b) => num(b.revenue) - num(a.revenue))
}

export function Overview() {
  const ctx = useFinance()
  const { range, previousRange, trend, selection, branch, branchName, scopeLabel, restricted, branchIgnored, go, drill, catalog, coverage, noCompare, gap, debtsExternalOnly } = ctx
  const source = useSalesSource()
  const cashCoverage = useCashCoverage()
  const cashNoCompareLive = noCompareReason(selection, cashCoverage)

  // Продажи: по направлениям (из них же итог), по группам, прошлый период, тренд.
  const byDirectionLive = useSales('direction', range)
  const byDirectionBeforeLive = useSales('direction', previousRange)
  const byGroup = useSales('nomenclature_group', range)
  const byBranch = useSales('branch', range)
  const salesTrendLive = useSales('period', trend)

  const cashLive = useCashByItem(range)
  const cashBeforeLive = useCashByItem(previousRange)
  const cashTrendLive = useCashTrend(trend)
  const debtors = useDebtors()
  const debtRelations = useDebtRelations()
  usePublishFreshness([byDirectionLive, cashLive, debtors])
  // Дебиторка своих организаций, не вошедшая в итог по настройке клиента.
  const ownDebtRows = (debtRelations.data?.rows ?? []).filter((row) => row.kind === 'Дебиторская' && row.relation === 'Между своими')
  const ownDebt = ctxOwn(ownDebtRows, 'amount')
  const ownOver60 = ctxOwn(ownDebtRows, 'overdue_61_90') + ctxOwn(ownDebtRows, 'overdue_90_plus')

  // Карточки: текущий период, прошлый и 12 месяцев тренда — одним набором
  // и с подписями того выбора, к которому они относятся. Пока набор для
  // нового периода не собран, на экране прежний, приглушённый.
  const sales = useSettled([byDirectionLive, byDirectionBeforeLive, salesTrendLive], { selection, noCompare, trend, range, coverage, scopeLabel })
  const [byDirection, byDirectionBefore, salesTrend] = sales.states
  const money3 = useSettled([cashLive, cashBeforeLive, cashTrendLive], { selection, noCompare: cashNoCompareLive, trend })
  const [cash, cashBefore, cashTrend] = money3.states
  const cashNoCompare = money3.view.noCompare
  const vs = compareLabel(sales.view.selection)

  const directions = marginRows(byDirectionLive)
  // У клиента три направления: три полоски — мало для главного графика
  // обзора, группы говорят больше. Направления — когда их хотя бы четыре.
  const autoDimension = directions.length >= 4 ? 'direction' : 'nomenclature_group'
  const [chosen, setChosen] = useState<string | null>(null)
  const dimension = chosen ?? autoDimension
  const chartLive = dimension === 'direction' ? byDirectionLive : dimension === 'branch' ? byBranch : byGroup

  const totals = byDirection.data?.totals ?? {}
  const before = byDirectionBefore.data?.totals ?? {}
  const months = monthsOf(sales.view.trend)
  const salesByMonth = indexBy(salesTrend.data?.rows, 'period')
  const cashMonths = monthsOf(money3.view.trend)
  const cashByMonth = indexBy(cashTrend.data?.rows, 'period')
  const rangeMonths = new Set(monthsOf(sales.view.range))
  const shownCoverage = sales.view.coverage

  // Тренд строк разреза за 12 месяцев — для мини-линий рядом с названием.
  const rowTrendLive = useReport(
    source?.code ?? 'margin_by_department',
    source && catalog.hasDimension(source.code, dimension)
      ? ctx.scoped(source.code, { period: trend, group_by: ['period', dimension], metrics: ['revenue'] })
      : null,
  )
  // Полосы разреза и их мини-тренды — тоже одним набором, вместе с разрезом.
  const chart = useSettled([chartLive, rowTrendLive], { dimension, trend })
  const [chartState, rowTrend] = chart.states
  const chartDimension = chart.view.dimension
  const chartRows = marginRows(chartState)
  const chartMonths = monthsOf(chart.view.trend)
  const trendOf = (key: string) => {
    if (rowTrend.status !== 'ready') return undefined
    const byMonth = new Map<string, number>()
    for (const row of rowTrend.data?.rows ?? []) {
      if (rowKey(row, chartDimension) !== key) continue
      byMonth.set(String(row.period).slice(0, 10), num(row.revenue))
    }
    return chartMonths.map((month) => (monthState(month, coverage) === 'data' ? (byMonth.get(month) ?? 0) : null))
  }

  const withMargin = source?.withMargin ?? false
  // Средняя — из того же ответа, что и строки разреза: итог отчёта не зависит от группировки,
  // а сигнал «ниже средней» не должен сравнивать строки одного периода со средней другого.
  const avgMargin = toNumber(chartState.data?.totals.margin_percent)

  // --- Долги ------------------------------------------------------------------
  const debtRows = debtors.data?.rows ?? []
  const debtTotals = debtors.data?.totals ?? {}
  const overdueTotal = overdueOf(debtTotals)
  const over60Total = num(debtTotals.overdue_61_90) + num(debtTotals.overdue_90_plus)
  const over60Count = debtRows.filter((row) => over60(row) > 0).length

  // --- Сигналы ----------------------------------------------------------------
  const signals: Signal[] = []
  const severity = { critical: 0, warning: 1, info: 2, positive: 3 }

  if (debtors.status === 'ready') {
    signals.push(
      over60Total > 0
        ? {
            key: 'overdue60',
            tone: 'critical',
            title: `Просрочено свыше 60 дней — ${money(over60Total)}`,
            text: `${pluralize(over60Count, 'контрагент', 'контрагента', 'контрагентов')}, из них свыше 90 дней — ${money(num(debtTotals.overdue_90_plus))}.${
              branchIgnored('receivables') ? ' По всей компании: долги не делятся по филиалам.' : ''
            }${debtsExternalOnly && ownOver60 > 0 ? ` Между своими ещё ${money(ownOver60)} — не включено.` : ''}`,
            rule: debtsExternalOnly
              ? 'Сумма корзин 61–90 и 90+ дебиторской задолженности внешних контрагентов на последнюю дату; долги своих организаций не включены'
              : 'Сумма корзин 61–90 и 90+ дебиторской задолженности на последнюю дату',
            onOpen: () => go('debts'),
          }
        : {
            key: 'overdue60',
            tone: 'positive',
            title: 'Просрочки свыше 60 дней нет',
            text: 'Ни один контрагент не просрочил оплату больше чем на два месяца.',
            rule: 'Корзины 61–90 и 90+ дебиторской задолженности пусты',
            onOpen: () => go('debts'),
          },
    )
  }

  const subject = chartDimension === 'direction' ? 'направление' : chartDimension === 'branch' ? 'филиал' : 'группа'
  if (withMargin && avgMargin !== null && chartState.status === 'ready' && chartRows.length >= 2) {
    const ranked = chartRows.filter((row) => toNumber(row.margin_percent) !== null)
    const lowest = [...ranked].sort((a, b) => num(a.margin_percent) - num(b.margin_percent))[0]
    const highest = [...ranked].sort((a, b) => num(b.margin_percent) - num(a.margin_percent))[0]

    if (lowest && num(lowest.margin_percent) < avgMargin) {
      signals.push({
        key: 'lowest',
        tone: 'warning',
        title: `${String(lowest[chartDimension])}: маржа ${formatPercent(lowest.margin_percent)}`,
        text: `Ниже средней ${branch || restricted ? 'по срезу' : 'по компании'} (${formatPercent(avgMargin)}) на ${formatNumber(Math.round((avgMargin - num(lowest.margin_percent)) * 10) / 10)} п.п.; выручка ${money(num(lowest.revenue))}.`,
        rule: `Самое низкомаржинальное ${subject === 'группа' ? 'из групп' : subject === 'филиал' ? 'из филиалов' : 'из направлений'} ниже средней`,
        onOpen: () => go('margin', { d: chartDimension, o: filterValue(lowest, chartDimension) }),
      })
    }
    if (highest && highest !== lowest) {
      signals.push({
        key: 'highest',
        tone: 'positive',
        title: `${String(highest[chartDimension])}: маржа ${formatPercent(highest.margin_percent)}`,
        text: `Самая высокая маржа за период; выручка ${money(num(highest.revenue))}.`,
        rule: `Наибольшая рентабельность среди: ${DIMENSION_TITLES[chartDimension]?.toLowerCase()}`,
        onOpen: () => go('margin', { d: chartDimension, o: filterValue(highest, chartDimension) }),
      })
    }
  }

  const withoutCost = num(totals.revenue_without_cost)
  const revenueTotal = num(totals.revenue)
  if (withMargin && revenueTotal > 0 && withoutCost / revenueTotal >= 0.01) {
    signals.push({
      key: 'without_cost',
      tone: 'info',
      title: `${formatPercent((withoutCost / revenueTotal) * 100)} выручки без себестоимости`,
      text: `${money(withoutCost)} — маржа по этой части не посчитана и в рентабельность не входит.`,
      rule: 'Выручка строк, по которым 1С не прислала себестоимость, к всей выручке',
      onOpen: () => go('pnl'),
    })
  }

  const net = toNumber(cash.data?.totals.net_flow)
  if (cash.status === 'ready' && net !== null && net < 0) {
    signals.push({
      key: 'cash_negative',
      tone: 'warning',
      title: `Чистый денежный поток отрицательный: ${money(net)}`,
      text: `Списания превысили поступления${branchIgnored('cashflow') ? ' (по всей компании — поток не делится по филиалам)' : ''}.`,
      rule: 'Поступления минус списания по всем статьям ДДС за период',
      onOpen: () => go('cash'),
    })
  }

  signals.sort((a, b) => severity[a.tone] - severity[b.tone])
  const signalsLoading = [byDirection, chartState, debtors, cash].some((state) => state.status === 'loading')

  // Две колонки обзора кончаются на одной высоте: «Динамика» слева
  // и «Дебиторка» справа занимают остаток своей колонки (график выше,
  // список крупнейших просрочек длиннее). При коротком разрезе (три
  // направления, как у клиента) и до шести строк сразу видно два сигнала,
  // а не три: иначе график пришлось бы растянуть вдвое. Остальные — по кнопке.
  const wide = useMediaQuery('(min-width: 1181px)')
  // Пока разрез не пришёл, скелетон рисует два сигнала — самый частый случай: без прыжка по приходу.
  const signalLimit = wide && (chartState.status !== 'ready' || chartRows.length <= 6) ? 2 : 3
  // Высота «Динамики» зависит от соседей, поэтому график появляется, когда
  // готовы все блоки обзора: иначе столбцы сдвигались бы при каждом ответе.
  const layoutStates = wide ? [chartState, debtors, cash, byDirection] : []
  const overdueRows = [...debtRows].filter((row) => overdueOf(row) > 0).sort((a, b) => overdueOf(b) - overdueOf(a))

  // Карточки появляются пачкой, каскадом по порядку, а не в порядке прихода ответов.
  const kpiBatch = [byDirection, cash, debtors].some((state) => state.status === 'loading')
  const kpiState = (status: ReportState['status']) => (kpiBatch && status === 'ready' ? 'loading' : status)

  const kpiCount = 1 + (withMargin ? 1 : 0) + (catalog.has('cashflow') ? 1 : 0) + (catalog.has('receivables') ? 1 : 0)

  const deltaOrIncomplete = (node: React.ReactNode, reason: string | null = sales.view.noCompare) =>
    reason ? <span className="muted">{reason}</span> : node
  const closedMonths = months.filter((month) => monthState(month, shownCoverage) === 'closed')
  // Прошлый месяц на мини-тренде — полым маркером; у квартала и года прошлый период не одна точка.
  const ghost = sales.view.selection.grain === 'month' ? months.length - 2 : undefined
  return (
    <div className="fin-stack">
      <div className={`kpi-grid kpi-grid--n${kpiCount}`}>
        <Kpi
          index={0}
          label="Выручка без НДС"
          memoryKey="ov.revenue"
          value={toNumber(totals.revenue)}
          format={money}
          state={source ? kpiState(byDirection.status) : 'idle'}
          retry={byDirection.retry}
          delta={deltaOrIncomplete(<Delta value={deltaPercent(totals.revenue, before.revenue)} label={vs} />)}
          hint={scopeLabel}
          spark={months.map((month) => valueIn(month, shownCoverage, salesByMonth.get(month)?.revenue))}
          sparkGhost={ghost}
          onOpen={() => go(withMargin ? 'pnl' : 'margin')}
        />
        {withMargin && <Kpi
          index={1}
          label="Валовая прибыль"
          memoryKey="ov.gp"
          value={toNumber(totals.gross_profit)}
          format={money}
          state={withMargin ? kpiState(byDirection.status) : 'forbidden'}
          retry={byDirection.retry}
          closedText="Маржа не входит в ваши права"
          delta={deltaOrIncomplete(<Delta value={deltaPercent(totals.gross_profit, before.gross_profit)} label={vs} />)}
          hint={
            <>
              Маржа {formatPercent(totals.margin_percent)}
              {!sales.view.noCompare && deltaPoints(totals.margin_percent, before.margin_percent) !== null && (
                <> · <Delta value={deltaPoints(totals.margin_percent, before.margin_percent)} points /></>
              )}
            </>
          }
          spark={months.map((month) => valueIn(month, shownCoverage, salesByMonth.get(month)?.gross_profit))}
          sparkGhost={ghost}
          sparkColor="var(--series-3)"
          onOpen={() => go('margin')}
        />}
        {catalog.has('cashflow') && <Kpi
          index={2}
          label="Чистый денежный поток"
          memoryKey="ov.net"
          value={net}
          format={money}
          state={catalog.has('cashflow') ? kpiState(cash.status) : 'forbidden'}
          retry={cash.retry}
          closedText="Движение денег не входит в ваши права"
          tag={branchIgnored('cashflow') ? 'вся компания' : undefined}
          tone={net !== null && net < 0 ? 'bad' : undefined}
          delta={deltaOrIncomplete(<MoneyDelta now={net} before={cashBefore.data?.totals.net_flow} label={vs} />, cashNoCompare)}
          hint="Поступления минус списания"
          spark={cashMonths.map((month) => valueIn(month, cashCoverage, cashByMonth.get(month)?.net_flow))}
          sparkGhost={ghost}
          onOpen={() => go('cash')}
        />}
        {catalog.has('receivables') && <Kpi
          index={3}
          label="Просроченная ДЗ"
          memoryKey="ov.overdue"
          value={debtors.status === 'ready' ? overdueTotal : null}
          format={money}
          state={catalog.has('receivables') ? kpiState(debtors.status) : 'forbidden'}
          retry={debtors.retry}
          closedText={closedReason(debtors, 'Задолженность не входит в ваши права')}
          tag={branchIgnored('receivables') ? 'вся компания' : undefined}
          tone={overdueTotal > 0 ? 'bad' : undefined}
          delta={
            <span className="muted">
              {num(debtTotals.amount) > 0
                ? `${formatPercent((overdueTotal / num(debtTotals.amount)) * 100)} от долга ${money(num(debtTotals.amount))}`
                : 'на последнюю дату'}
            </span>
          }
          hint={over60Total > 0 ? `Свыше 60 дней — ${money(over60Total)}` : 'На последнюю дату снимка'}
          onOpen={() => go('debts')}
        />}
      </div>

      {ctx.partial && <Caveat tone="warn">{ctx.partial}</Caveat>}

      <div className="fin-grid fin-grid--main">
        <div className="fin-col fin-col--main">
          <Panel
            index={1}
            title={withMargin ? 'Выручка и валовая прибыль' : 'Выручка без НДС'}
            subtitle={`${DIMENSION_TITLES[chartDimension]} · без НДС${withMargin ? ' · прибыль — по строкам с себестоимостью' : ''}`}
            states={[chartState]}
            skeleton={<SkeletonBars rows={5} series={withMargin ? 2 : 1} />}
            empty={salesEmpty(ctx, chartRows.length === 0)}
            actions={
              <Segmented
                size="sm"
                label="Разрез"
                value={dimension}
                onChange={setChosen}
                options={[
                  { value: 'direction', label: 'Направления' },
                  { value: 'nomenclature_group', label: 'Группы' },
                  ...(branch ? [] : [{ value: 'branch', label: 'Филиалы' }]),
                ]}
              />
            }
          >
            {() => (
              <>
                {chartDimension === 'direction' && chartRows.length === 1 && (
                  <Caveat>В срезе одно направление — разбивка по группам показывает больше.</Caveat>
                )}
                <BarRows
                  ariaLabel="Выручка и валовая прибыль"
                  series={withMargin ? SALES_SERIES : SALES_SERIES.slice(0, 1)}
                  rows={chartRows.slice(0, 8).map((row) => ({
                    key: rowKey(row, chartDimension),
                    label: String(row[chartDimension] ?? '—'),
                    sub: withMargin ? `маржа ${formatPercent(row.margin_percent)}` : undefined,
                    values: withMargin ? [num(row.revenue), toNumber(row.gross_profit)] : [num(row.revenue)],
                    trend: trendOf(rowKey(row, chartDimension)),
                  }))}
                  onSelect={(row, element) =>
                    drill({ kind: 'sales', dimension: chartDimension, value: row.key, label: row.label }, originFrom(element))
                  }
                />
                {chartRows.length > 8 && (
                  <button type="button" className="link-button" onClick={() => go('margin', { d: chartDimension })}>
                    Ещё {chartRows.length - 8} — в разделе «Маржинальность» <Icon name="arrow-right" size={14} />
                  </button>
                )}
              </>
            )}
          </Panel>

          <Panel
            index={3}
            title="Динамика за 12 месяцев"
            subtitle={`${monthLabel(sales.view.trend.from)} — ${monthLabel(months[months.length - 1] ?? sales.view.trend.to)} · выбранный период подсвечен${
              closedMonths.length > 0 && shownCoverage.notBefore ? ` · вам доступны данные с ${formatDate(shownCoverage.notBefore)}` : ''
            }`}
            states={[salesTrend, ...layoutStates]}
            skeleton={<SkeletonColumns height={180} />}
            fill
          >
            {() => (
              <ColumnChart
                ariaLabel="Выручка и валовая прибыль по месяцам"
                height={180}
                fill={wide ? { min: 180, max: 360 } : undefined}
                emptyText={
                  closedMonths.length === months.length && shownCoverage.notBefore
                    ? `Эти 12 месяцев раньше окна ваших прав: вам доступны данные с ${formatDate(shownCoverage.notBefore)}`
                    : 'Продаж за эти 12 месяцев в выгрузке нет'
                }
                series={withMargin ? SALES_SERIES : SALES_SERIES.slice(0, 1)}
                format={formatMoneyShort}
                summaryTitle={labelOf(sales.view.selection)}
                points={months.map((month) => ({
                  key: month,
                  label: shortMonth(month),
                  title: monthLabel(month),
                  highlight: rangeMonths.has(month),
                  missing: missingOf(month, shownCoverage),
                  values: withMargin
                    ? [num(salesByMonth.get(month)?.revenue), num(salesByMonth.get(month)?.gross_profit)]
                    : [num(salesByMonth.get(month)?.revenue)],
                }))}
              />
            )}
          </Panel>
        </div>

        <div className="fin-col">
          <section className="panel cascade" style={{ '--i': 2 } as React.CSSProperties} aria-busy={signalsLoading}>
            <header className="panel__header">
              <div className="panel__heading">
                <h3 className="panel__title">Сигналы</h3>
                <div className="panel__subtitle">Вычислены из отчётов по правилу, указанному под каждым</div>
              </div>
            </header>
            <div className="panel__body">
              {signalsLoading ? (
                <SkeletonSignals count={signalLimit} />
              ) : signals.length === 0 ? (
                <div className="panel__state">
                  <div className="strong secondary">Сигналов нет</div>
                  <div className="small muted">
                    {gap ? 'Правила по продажам не проверялись: за период нет загруженных данных.' : 'Ни одно правило не сработало на данных периода.'}
                  </div>
                </div>
              ) : (
                <SignalList signals={signals} limit={signalLimit} />
              )}
            </div>
          </section>
          {catalog.has('receivables') && <Panel
            index={4}
            title="Дебиторка по срокам"
            subtitle={`На последнюю дату${branchIgnored('receivables') ? ' · вся компания, по филиалам не делится' : ''}`}
            states={[debtors]}
            skeleton={<SkeletonAgeing />}
            fill
            empty={{ when: num(debtTotals.amount) === 0, title: 'Дебиторской задолженности нет', hint: 'На последнюю дату снимка никто не должен.' }}
            actions={
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => go('debts')}>
                Подробно <Icon name="arrow-right" size={14} />
              </button>
            }
          >
            {() => (
              <div className="stack stack--md fill-stack">
                <div className="ageing-head">
                  <span className="mono strong" style={{ fontSize: 'var(--text-xl)' }}>
                    {money(num(debtTotals.amount))}
                  </span>
                  <span className="muted small">
                    {debtsExternalOnly ? 'внешние контрагенты' : 'всего'}, из них просрочено {formatPercent((overdueTotal / Math.max(1, num(debtTotals.amount))) * 100)}
                  </span>
                </div>
                {debtsExternalOnly && ownDebt > 0 && (
                  <div className="small muted">Между своими: {money(ownDebt)} (не включено)</div>
                )}
                <SegmentBar
                  ariaLabel="Структура дебиторской задолженности по срокам"
                  segments={BUCKETS.map((bucket) => ({ key: bucket.key, label: bucket.label, color: bucket.color, value: num(debtTotals[bucket.key]) }))}
                />
                <ul className="ageing-legend">
                  {BUCKETS.map((bucket) => (
                    <li key={bucket.key}>
                      <span className="chart-legend__swatch" style={{ background: bucket.color }} aria-hidden="true" />
                      <span className="secondary">{bucket.label}</span>
                      <span className="mono strong">{money(num(debtTotals[bucket.key]))}</span>
                    </li>
                  ))}
                </ul>
                {wide && overdueRows.length > 0 && (
                  <FillRows row={36} reserve={30}>
                    {(count) =>
                      count > 0 && (
                        <div className="top-overdue">
                          <div className="top-overdue__title">Больше всего просрочено</div>
                          {overdueRows.slice(0, Math.min(count, 6)).map((row, index) => (
                            <button
                              key={rowKey(row, 'counterparty')}
                              type="button"
                              className="top-overdue__row cascade"
                              style={{ '--i': Math.min(index, 7) } as React.CSSProperties}
                              onClick={(event) =>
                                drill({ kind: 'debtor', value: rowKey(row, 'counterparty'), label: String(row.counterparty ?? '—') }, originFrom(event.currentTarget))
                              }
                            >
                              <span className="top-overdue__name">{String(row.counterparty ?? '—')}</span>
                              <span className="mono strong">{money(overdueOf(row))}</span>
                            </button>
                          ))}
                        </div>
                      )
                    }
                  </FillRows>
                )}
              </div>
            )}
          </Panel>}
        </div>
      </div>


      {/* Под сеткой, а не над ней: при смене города оговорка не сдвигает
          панели. На месте об этом говорит пометка «вся компания» на карточках. */}
      {(branchIgnored('cashflow') || branchIgnored('receivables')) && (
        <div className="fin-appear">
          <Caveat>
            Движение денег и долги в 1С не привязаны к городу, поэтому карточки и блоки с пометкой «вся компания»
            показывают всю компанию — фильтр «{branchName ?? 'выбранный город'}» к ним не применяется.
          </Caveat>
        </div>
      )}

      <DataNotes states={[byDirection, chartState, salesTrend, cash, debtors]} />
    </div>
  )
}

/** Скелетон «Дебиторки по срокам»: сумма, полоса и пять строк легенды на своих местах. */
function SkeletonAgeing() {
  return (
    <div className="stack stack--md" aria-busy="true" aria-label="Загрузка">
      <div className="ageing-head">
        <span className="mono strong" style={{ fontSize: 'var(--text-xl)', width: '40%' }}>
          <span className="skeleton skeleton--text" style={{ width: '100%' }} />
        </span>
      </div>
      <div className="small"><span className="skeleton skeleton--text" style={{ width: '60%' }} /></div>
      <div className="skeleton" style={{ height: 18 }} />
      <ul className="ageing-legend">
        {Array.from({ length: 5 }, (_, index) => (
          <li key={index}>
            <span className="skeleton" style={{ width: 10, height: 10 }} />
            <span><span className="skeleton skeleton--text" style={{ width: `${50 - index * 4}%` }} /></span>
            <span><span className="skeleton skeleton--text" style={{ width: 56 }} /></span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function ctxOwn(rows: Record<string, unknown>[], key: string): number {
  return rows.reduce((sum, row) => sum + num(row[key]), 0)
}
